import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { DatasetName } from "@/common/constants.ts";
import type { DatabaseFieldConfig, ParitaWorkspace } from "@/common/user";
import { REPORT_PRESETS } from "@/common/report-configuration";
import { MEASURES } from "@/common/measures";
import { DIMENSIONS } from "@/common/dimensions";
import { FILTERS } from "@/common/filters";

/* eslint @typescript-eslint/no-explicit-any: 0 */

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatAxisValue = (value: number): string => {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  } else if (value >= 10_000) {
    return `${(value / 1_000).toFixed(0)}k`;
  }
  return value.toLocaleString();
};

export const properCase = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// A temporary hack to handle returning pounds instead of USD for instance 00031
export function getCurrency(
  workspace: ParitaWorkspace,
  type: "symbol" | "iso",
) {
  switch (type) {
    case "symbol":
      if (workspace.domo_instance_domain === "parity-org-00031.domo.com") {
        return "£";
      }
      return "$";
    case "iso":
      if (workspace.domo_instance_domain === "parity-org-00031.domo.com") {
        return "GBP";
      }
      return "USD";
    default:
      return "$";
  }
}

// This function filters report preset groups based on the workspace's dataset field configuration
// so that presets that include measures, dimensions, or filters that reference empty fields are excluded.
export const disabledReportPresetsFromDatasetConfig = (
  presetGroups: Record<string, Array<keyof typeof REPORT_PRESETS>>,
  datasetsConfig: Record<DatasetName, DatabaseFieldConfig>,
): Array<keyof typeof REPORT_PRESETS> => {
  const disabledPresets: Array<keyof typeof REPORT_PRESETS> = [];
  Object.entries(presetGroups).forEach(([, presetKeys]) => {
    presetKeys.forEach((presetKey) => {
      const preset = REPORT_PRESETS[presetKey].config.data;
      const fieldConfig = datasetsConfig[preset.datasetName];

      if (!fieldConfig) {
        // If there's no field config for this dataset, we can't determine emptiness, so skip.
        return;
      }
      // Check measures
      if (
        preset.measures.some((m) =>
          fieldConfig.emptyFields.some((field) =>
            MEASURES[m].columnsReferenced.includes(field),
          ),
        )
      ) {
        disabledPresets.push(presetKey);
        return;
      }

      // check dimensions
      if (
        preset.dimensions.some((d) =>
          fieldConfig.emptyFields.includes(DIMENSIONS[d].field as any),
        )
      ) {
        disabledPresets.push(presetKey);
        return;
      }

      // check filters
      if (
        preset.filters.some((f) =>
          fieldConfig.emptyFields.includes(
            FILTERS[f.filterDefinitionName].field as any,
          ),
        )
      ) {
        disabledPresets.push(presetKey);
        return;
      }
    });
  });
  return disabledPresets;
};
